import { ScreenLayout } from 'src/shared/layout/ScreenLayout'
import { PageLayout } from 'src/shared/layout/PageLayout'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { CreateButton } from 'src/shared/button/Buttons'
import { useClient } from 'urql'
import { useDelayedNavigate } from 'src/shared/utils/hooks/navigation-hooks'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { useCallback, useEffect, useState } from 'react'
import { ROUTES } from 'src/routing/routes'
import {
  FeatureGroupsConfigFormValues,
  FeaturesConfigurationFeatureGroupsForm,
} from 'src/screens/administration/configuration-management/features-configuration/feature-groups/edit/FeaturesConfigurationFeatureGroupsForm'
import { Feature_Group_Config_Insert_Input, Feature_Type_Config, Mutation_Root } from 'src/@types/graphql'
import { CONFIGURATION_STATUS } from 'src/shared/constants/constants'
import { insertFeatureGroupConfig } from 'src/screens/administration/configuration-management/features-configuration/feature-groups/featureGroupsConfigQueries'
import { fetchAllFeatureTypes } from 'src/screens/administration/configuration-management/features-configuration/feature-groups/edit/FeaturesConfigurationFeatureGroupsFormUtils'
import { useUserLocale } from 'src/user/UserContext'

const defaultInitialValues: FeatureGroupsConfigFormValues = {
  enName: '',
  deName: '',
  frName: '',
  itName: '',
  status: CONFIGURATION_STATUS.ACTIVE,
  featureTypeId: null,
}

export const FeaturesConfigurationFeatureGroupsCreatePage = () => {
  const { getMessage } = useMessageSource()
  const urqlClient = useClient()
  const navigate = useDelayedNavigate()
  const notificationService = useNotificationService()
  const locale = useUserLocale()

  const [initialValues, setInitialValues] = useState<FeatureGroupsConfigFormValues | null>(null)
  const [formDirty, setFormDirty] = useState<boolean | undefined>(undefined)

  const [featureTypes, setFeatureTypes] = useState<Feature_Type_Config[]>([])

  useEffect(() => {
    setInitialValues(defaultInitialValues)
  }, [])

  const onBackHandler = useCallback(() => {
    navigate(ROUTES.FeaturesConfigurationIndex.nested.FeaturesConfigurationFeatureGroups.params({}))
  }, [navigate])

  const onCreate = async () => {
    document.getElementById('feature-group-config-form')?.dispatchEvent(
      new Event('submit', {
        cancelable: true,
        bubbles: true,
      }),
    )
  }

  const handleSubmitLocal = useCallback(
    async (values: FeatureGroupsConfigFormValues) => {
      if (!values.featureTypeId) {
        notificationService.operationFailed()
        return
      }

      const featureGroupInsertInput: Feature_Group_Config_Insert_Input = {
        code: crypto.randomUUID(),
        names: { de: values.deName, en: values.enName, fr: values.frName, it: values.itName },
        active: values.status === CONFIGURATION_STATUS.ACTIVE,
        feature_type_id: values.featureTypeId,
      }

      const { data, error } = await urqlClient
        .mutation<{
          insert_feature_group_config: Mutation_Root['insert_feature_group_config']
        }>(insertFeatureGroupConfig, {
          featureGroupInsertInput,
        })
        .toPromise()

      if (!error && data?.insert_feature_group_config?.affected_rows === 1) {
        notificationService.changesSaved()
        setFormDirty(false)
        onBackHandler()
      } else {
        notificationService.operationFailed()
      }
    },
    [notificationService, onBackHandler, urqlClient],
  )

  const initFeatureTypes = useCallback(async () => {
    const { fetchedFeatureTypes } = await fetchAllFeatureTypes(urqlClient, locale)()
    setFeatureTypes(fetchedFeatureTypes)
  }, [urqlClient, locale])

  useEffect(() => {
    initFeatureTypes()
  }, [initFeatureTypes])

  return (
    <ScreenLayout
      title={getMessage('label.configuration.features.group.create')}
      onBack={onBackHandler}
      hasSecondLevelNavigation={false}
      actions={<CreateButton origin="header" onClick={onCreate} />}
    >
      <PageLayout>
        <>
          {initialValues && (
            <FeaturesConfigurationFeatureGroupsForm
              mode="CREATE"
              onSubmit={handleSubmitLocal}
              initialValues={initialValues}
              featureTypes={featureTypes}
              formDirty={formDirty}
            />
          )}
        </>
      </PageLayout>
    </ScreenLayout>
  )
}
